import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { Entity } from '@shared/constants/entity';
import { HttpErrorModel } from '@shared/interfaces/http-error.model';
import { TranslateService } from '@ngx-translate/core';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, EMPTY, Observable, of, switchMap, throwError } from 'rxjs';
import { DialogService } from '@core/services/dialog.service';
import { DvDialogComponent } from '@devinco/ui/dds';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';

const IgnoredApiUrlParts = [
  '/UserSetting',
  '/connect/token',
  '/Text',
  '/Version/web',
];

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private oidcSecurityService = inject(OidcSecurityService);
  private snackBar = inject(DvSnackbarService);
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private rolesAndPermissionsService = inject(UserRolesAndPermissionsService);
  private dialog = inject(DialogService);

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError(resp => {
        if (resp.url.includes(Entity.UserEventLog)) {
          return throwError(() => resp);
        }

        if (
          request.url.includes('connect/token') ||
          request.url.includes('silent-renew.html')
        ) {
          if (resp.error?.error === 'invalid_grant') {
            this.oidcSecurityService.logoff().subscribe();
          }
          return of();
        }

        switch (resp.status) {
          case 401:
            this.oidcSecurityService.logoff().subscribe();
            return EMPTY;
          case 403: {
            if (
              resp.error?.vtUserExists &&
              resp.error.vtUserExists === 'false'
            ) {
              return this.openUserDoesNotExistDialog(
                resp.error?.textId
              ).closed.pipe(
                switchMap(() => {
                  this.oidcSecurityService.logoff().subscribe();
                  return of();
                })
              );
            } else {
              this.checkPermissions();
              return throwError(() => resp);
            }
          }
          default:
            if (!IgnoredApiUrlParts.some(part => resp.url?.includes(part))) {
              this.snackBar.open(
                this.getErrorMessage(resp.error),
                SnackBarConfig.Error
              );
            }
            return throwError(() => resp);
        }
      })
    );
  }

  private getErrorMessage(error?: HttpErrorModel): string {
    if (error?.textId) return this.translateService.instant(error.textId);

    if (error?.message) return error.message;

    return this.translateService.instant('General.Error');
  }

  private refreshApplication(): void {
    window.location.replace(window.location.origin + '/trips');
  }

  private checkPermissions(): void {
    this.rolesAndPermissionsService
      .checkIfAccessControlRestrictionsChanged()
      .subscribe(hasPermissionsChanged => {
        if (hasPermissionsChanged) {
          this.refreshApplication();
        } else {
          this.router.navigate(['/access-denied']);
        }
      });
  }

  private openUserDoesNotExistDialog(
    textId?: string
  ): DvDialogComponent<undefined, undefined> {
    return this.dialog.open({
      type: 'error',
      title: 'General.Error.UserDoesNotExistTitle',
      contentText: textId || 'General.Error.UserDoesNotExistContent',
      isDismissible: false,
      size: 'sm',
      confirmText: 'General.Ok',
    });
  }
}
